import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { AGE_RANGE_IDS, QUESTION_IDS } from "@/config/site";
import { SessionPicker } from "./SessionPicker";

export function StepBar({ step, labels, sfx = "" }) {
  return (
    <div className="reg-steps" data-testid={`reg-step-bar${sfx}`}>
      {labels.map((label, index) => {
        const number = index + 1;
        const state = number < step ? "done" : number === step ? "active" : "";
        return (
          <div key={label} className="contents">
            {index > 0 && <span className="reg-step-line" aria-hidden="true" />}
            <div className={`reg-step ${state}`} data-testid={`reg-step-indicator-${number}${sfx}`}>
              <span className="reg-step-no">{number}</span>
              <span>{label}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function StepOne({ r, form, set, onNext, sfx = "" }) {
  return (
    <div className="reg-stack" data-testid={`reg-step-1${sfx}`}>
      <div className="reg-field">
        <label className="reg-label" htmlFor={`fullName${sfx}`}>{r.labels.name}</label>
        <input id={`fullName${sfx}`} className="reg-input" type="text" autoComplete="name" placeholder={r.placeholders.name} value={form.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid={`reg-name-input${sfx}`} />
      </div>
      <div className="reg-field">
        <label className="reg-label" htmlFor={`mobile${sfx}`}>{r.labels.mobile}</label>
        <div className="reg-phone">
          <span className="reg-prefix" data-testid={`reg-mobile-prefix${sfx}`}>{r.mobilePrefix}</span>
          <input id={`mobile${sfx}`} className="reg-input" type="tel" inputMode="numeric" autoComplete="tel-national" maxLength={9} placeholder={r.placeholders.mobile} value={form.mobile} onChange={(e) => set("mobile", e.target.value.replace(/[^\d\s]/g, ""))} data-testid={`reg-mobile-input${sfx}`} />
        </div>
      </div>
      <div className="reg-field">
        <label className="reg-label" htmlFor={`email${sfx}`}>{r.labels.email}</label>
        <input id={`email${sfx}`} className="reg-input" type="email" autoComplete="email" placeholder={r.placeholders.email} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid={`reg-email-input${sfx}`} />
      </div>
      <button type="button" className="reg-btn" onClick={onNext} data-testid={`reg-continue-btn${sfx}`}>
        {r.continue} <ArrowRight size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

export function StepTwo({ r, form, set, onBack, submitting, togglePill, consentError, ageError, sfx = "", lockedInvitedBy = false }) {
  const pills = QUESTION_IDS.filter((id) => id !== "other");
  return (
    <div className="reg-stack" data-testid={`reg-step-2${sfx}`}>
      <div className="reg-field">
        <span className="reg-label">{r.labels.session}</span>
        <SessionPicker idPrefix={`session${sfx}`} light />
      </div>
      <div className="reg-field">
        <span className="reg-label" id={`ageRangeLabel${sfx}`}>{r.labels.ageRange}<span className="req"> *</span></span>
        <div className={`reg-pills ${ageError ? "error" : ""}`} role="radiogroup" aria-labelledby={`ageRangeLabel${sfx}`} data-testid={`reg-age-range-group${sfx}`}>
          {AGE_RANGE_IDS.map((id) => (
            <button key={id} type="button" role="radio" aria-checked={form.age_range === id} className={`reg-pill ${form.age_range === id ? "active" : ""}`} onClick={() => set("age_range", id)} data-testid={`reg-age-pill-${id}${sfx}`}>
              {r.ageRanges[id]}
            </button>
          ))}
        </div>
      </div>
      <div className="reg-field">
        <label className="reg-label" htmlFor={`mainQuestion${sfx}`}>{r.labels.question} <span className="opt">{r.optional}</span></label>
        <div className="reg-pills">
          {pills.map((id) => (
            <button key={id} type="button" className={`reg-pill ${form.question === r.questions[id] ? "active" : ""}`} onClick={() => togglePill(id)} data-testid={`reg-question-pill-${id}${sfx}`}>
              {r.questions[id]}
            </button>
          ))}
        </div>
        <textarea id={`mainQuestion${sfx}`} className="reg-input" rows={3} maxLength={500} placeholder={r.placeholders.other} value={form.question} onChange={(e) => set("question", e.target.value)} data-testid={`reg-question-input${sfx}`} />
      </div>
      <div className="reg-field">
        <label className="reg-label" htmlFor={`invitedBy${sfx}`}>{r.labels.invitedBy} <span className="opt">{r.optional}</span></label>
        <input
          id={`invitedBy${sfx}`}
          className={`reg-input ${lockedInvitedBy ? "reg-input-locked" : ""}`}
          type="text"
          maxLength={120}
          placeholder={r.placeholders.invitedBy}
          value={form.invited_by}
          onChange={(e) => !lockedInvitedBy && set("invited_by", e.target.value)}
          readOnly={lockedInvitedBy}
          aria-readonly={lockedInvitedBy}
          tabIndex={lockedInvitedBy ? -1 : 0}
          title={lockedInvitedBy ? r.invitedByLocked : undefined}
          data-testid={`reg-invited-by-input${sfx}`}
        />
        {lockedInvitedBy && <span className="reg-hint" data-testid={`reg-invited-by-locked${sfx}`}>{r.invitedByLocked}</span>}
      </div>
      <div className={`data-consent ${consentError ? "error" : ""}`} role="note" data-testid={`pdpa-notice${sfx}`}>
        <p>{r.pdpa.intro}</p>
        <ul>
          {r.pdpa.purposes.map((purpose) => (
            <li key={purpose}>{purpose}</li>
          ))}
        </ul>
        <p>
          {r.pdpa.withdraw}
          <a href={`mailto:${r.pdpa.email}`} data-testid={`pdpa-withdraw-email${sfx}`}>{r.pdpa.email}</a>.
        </p>
        <label className="data-consent-check">
          <input type="checkbox" name="consent" checked={form.consent} onChange={(e) => set("consent", e.target.checked)} aria-required="true" data-testid={`reg-consent-checkbox${sfx}`} />
          <span>{r.consent}<span className="req"> *</span></span>
        </label>
      </div>
      <div className="reg-actions">
        <button type="button" className="reg-btn reg-btn-outline" onClick={onBack} data-testid={`reg-back-btn${sfx}`}>
          <ArrowLeft size={18} aria-hidden="true" /> {r.back}
        </button>
        <button type="submit" className="reg-btn" disabled={submitting} data-testid={`reg-submit-btn${sfx}`}>
          {submitting ? r.messages.submitting : (<>{r.submit} <Check size={18} aria-hidden="true" /></>)}
        </button>
      </div>
    </div>
  );
}
